import { useEffect, useState } from "react";
import { FacetedScene } from "./components/FacetedScene";
import { FacetedBackdrop } from "./components/AmbientFacets";
import { PipelineGraphic } from "./components/PipelineGraphic";
import { FourArmStudy, PrimaryComparison } from "./components/EvidenceVisuals";
import { pilotContactUrl, siteConfig } from "./config";

const navigation = [
  ["Thesis", "#thesis"],
  ["MAVERICK", "#maverick"],
  ["Evidence", "#evidence"],
  ["Pilot", "#pilot"],
  ["Research", "#research"],
];

const pilotSteps = [
  {
    number: "01",
    title: "Define the target behavior",
    copy: "Select the target behavior, baseline, evaluation set and success metric.",
  },
  {
    number: "02",
    title: "Build validated supervision",
    copy: "Generate and validate task-side supervision for the agreed objective.",
  },
  {
    number: "03",
    title: "Train the checkpoint",
    copy: "Run the controlled training experiment on the selected model.",
  },
  {
    number: "04",
    title: "Measure the operating-point change",
    copy: "Compare the resulting model against the same fixed baseline and evaluation set.",
  },
];

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a className={`brand${compact ? " brand-compact" : ""}`} href="#top" aria-label="Keywan home">
      <img src="/keywan-mark-lossless.webp" alt="" width="50" height="50" />
      <span>Keywan</span>
    </a>
  );
}

function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return <span aria-hidden="true" className="button-arrow">{diagonal ? "↗" : "→"}</span>;
}

type FooterIconName = "research" | "dataset" | "github" | "linkedin" | "contact";

function FooterIcon({ name }: { name: FooterIconName }) {
  const paths = {
    research: (
      <>
        <path d="M3 4h6a4 4 0 0 1 4 4v12a3.5 3.5 0 0 0-3-3H3Z" />
        <path d="M21 4h-6a4 4 0 0 0-4 4v12a3.5 3.5 0 0 1 3-3h7Z" />
      </>
    ),
    dataset: (
      <>
        <ellipse cx="12" cy="5" rx="8.5" ry="3" />
        <path d="M3.5 5v7c0 1.7 3.8 3 8.5 3s8.5-1.3 8.5-3V5" />
        <path d="M3.5 12v7c0 1.7 3.8 3 8.5 3s8.5-1.3 8.5-3v-7" />
      </>
    ),
    github: (
      <>
        <path d="M15 22v-3.9a4.8 4.8 0 0 0-1-3.5c3.3-.4 6.7-1.6 6.7-7A5.4 5.4 0 0 0 19.2 3.8 5 5 0 0 0 19.1.1 7.8 7.8 0 0 0 15 1.5a13.4 13.4 0 0 0-6 0A7.8 7.8 0 0 0 4.9.1a5 5 0 0 0-.1 3.7A5.4 5.4 0 0 0 3.3 7.5c0 5.4 3.4 6.6 6.7 7A4.8 4.8 0 0 0 9 18.1V22" />
        <path d="M9 18c-4.5 2-5-2-7-2" />
      </>
    ),
    linkedin: (
      <>
        <rect x="3" y="9" width="4" height="12" />
        <circle cx="5" cy="4" r="2" />
        <path d="M11 21v-7a5 5 0 0 1 10 0v7h-4v-7a1 1 0 0 0-2 0v7Z" />
      </>
    ),
    contact: (
      <>
        <rect x="2.5" y="4.5" width="19" height="15" rx="1" />
        <path d="m3 6 9 7 9-7" />
      </>
    ),
  };

  return (
    <svg className="footer-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[name]}
    </svg>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 28);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -9%", threshold: 0.08 },
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const externalContact = pilotContactUrl.startsWith("http");

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <header className={`site-header${scrolled ? " is-scrolled" : ""}`}>
        <div className="header-inner">
          <Brand compact />
          <nav className="desktop-nav" aria-label="Primary navigation">
            {navigation.map(([label, href]) => (
              <a key={href} href={href}>{label}</a>
            ))}
          </nav>
          <a
            className="header-cta"
            href={pilotContactUrl}
            target={externalContact ? "_blank" : undefined}
            rel={externalContact ? "noopener noreferrer" : undefined}
          >
            Discuss a pilot <Arrow diagonal={externalContact} />
          </a>
          <button
            className="menu-toggle"
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((value) => !value)}
          >
            <span />
            <span />
          </button>
        </div>
        <nav
          id="mobile-navigation"
          className={`mobile-nav${menuOpen ? " is-open" : ""}`}
          aria-label="Mobile navigation"
        >
          {navigation.map(([label, href]) => (
            <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}</a>
          ))}
          <a className="mobile-nav-cta" href={pilotContactUrl} target={externalContact ? "_blank" : undefined} rel={externalContact ? "noopener noreferrer" : undefined}>
            Discuss a pilot <Arrow diagonal={externalContact} />
          </a>
        </nav>
      </header>

      <main id="main">
        <section className="hero" id="top" aria-labelledby="hero-title">
          <div className="hero-scrim" />
          <FacetedScene />
          <div className="shell hero-inner">
            <div className="hero-copy reveal is-visible">
              <p className="eyebrow">MODEL BEHAVIOR</p>
              <h1 id="hero-title">Teach models when and how much to think.</h1>
              <p className="hero-support">
                Keywan helps model teams move suitable runtime control into learned model behavior.
              </p>
              <div className="hero-actions">
                <a
                  className="button button-primary"
                  href={pilotContactUrl}
                  target={externalContact ? "_blank" : undefined}
                  rel={externalContact ? "noopener noreferrer" : undefined}
                >
                  Discuss a pilot <Arrow diagonal={externalContact} />
                </a>
                <a className="button button-secondary" href="#maverick">
                  Explore MAVERICK <Arrow />
                </a>
              </div>
            </div>

            <div className="proof-strip reveal" aria-label="Initial controlled evaluation results">
              <p>MAVERICK · INITIAL CONTROLLED EVALUATION</p>
              <dl>
                <div><dt>24.3%</dt><dd>fewer reasoning tokens</dd></div>
                <div><dt>15.7%</dt><dd>fewer output tokens</dd></div>
                <div><dt>400</dt><dd>held-out examples</dd></div>
              </dl>
            </div>
          </div>
        </section>

        <section className="section thesis-section" id="thesis" aria-labelledby="thesis-title">
          <div className="shell">
            <div className="section-heading thesis-heading reveal">
              <p className="eyebrow">THE COMPANY THESIS</p>
              <h2 id="thesis-title">Some model behavior is paid for on every inference.</h2>
            </div>
            <div className="thesis-body reveal">
              <div className="body-copy">
                <p>
                  AI teams use prompts, routers, reasoning budgets, decoding policies and other runtime infrastructure to control model behavior.
                </p>
                <p>
                  Some of that control may not need to be paid for on every request. Keywan tests whether measurable runtime behavior can instead be learned by the model.
                </p>
              </div>
              <div className="thesis-diagram" aria-label="Today, runtime control repeats during inference. Keywan’s approach tests whether validated supervision and training can turn suitable control into learned behavior.">
                <article>
                  <span>TODAY</span>
                  <div className="diagram-flow">
                    <b>Model</b><i aria-hidden="true">→</i><b>Runtime control</b><i aria-hidden="true">→</i><b>Inference</b>
                  </div>
                  <small>Control repeats at runtime</small>
                </article>
                <article className="approach-flow">
                  <span>KEYWAN’S APPROACH</span>
                  <div className="diagram-flow">
                    <b>Validated supervision</b><i aria-hidden="true">→</i><b>Training</b><i aria-hidden="true">→</i><b>Learned behavior</b>
                  </div>
                  <small>Test whether suitable control can be learned once.</small>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="section maverick-section" id="maverick" aria-labelledby="maverick-title">
          <FacetedBackdrop className="section-crystal section-crystal-left" variant="maverick" />
          <div className="shell">
            <div className="split-heading reveal">
              <div>
                <p className="eyebrow">FIRST SYSTEM</p>
                <h2 id="maverick-title">MAVERICK is Keywan’s first system for learning reasoning-budget behavior.</h2>
              </div>
              <p>
                It creates validated task-side supervision so reasoning-budget behavior can be learned before deployment instead of being repeatedly enforced during inference.
              </p>
            </div>

            <aside className="upstream-supervision reveal" aria-labelledby="upstream-title">
              <div>
                <p className="eyebrow">UPSTREAM SUPERVISION</p>
                <h3 id="upstream-title">Represent the reasoning demand before training the model.</h3>
              </div>
              <div className="upstream-copy">
                <p>
                  MAVERICK constructs a structured, multi-axis task profile before student-model optimization. Controlled interventions vary the task’s presentation and profile while preserving its semantic kernel, creating reasoning supervision independently of student rollouts or runtime behavior.
                </p>
                <small>The four reasoning modes are an initial discretization of a potentially continuous reasoning-demand representation.</small>
              </div>
            </aside>

            <PipelineGraphic />

            <div className="dataset-readout reveal">
              <dl>
                <div><dt>1,222</dt><dd>source problems</dd></div>
                <div><dt>1,180</dt><dd>validated variants</dd></div>
                <div><dt>42</dt><dd>persistent failures</dd></div>
                <div><dt>96.6%</dt><dd>pipeline acceptance</dd></div>
              </dl>
              <p>
                Acceptance measures validation-process completion, not human semantic accuracy.
              </p>
              <div className="text-links">
                <a href={siteConfig.datasetUrl} target="_blank" rel="noopener noreferrer">View the dataset <Arrow diagonal /></a>
                <span className="disabled-preprint" role="link" aria-disabled="true">Preprint available soon</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section evidence-section" id="evidence" aria-labelledby="evidence-title">
          <div className="shell">
            <div className="evidence-heading reveal">
              <p className="eyebrow">CONTROLLED EVIDENCE</p>
              <h2 id="evidence-title">24.3% lower reasoning expenditure, without observed accuracy loss.</h2>
              <p>
                Gemma 4 E2B · 400 held-out examples · Adaptive LoRA vs. untuned Base Thinking.
              </p>
            </div>

            <div className="reveal">
              <PrimaryComparison />
            </div>

            <div className="evidence-qualification reveal">
              <article>
                <p className="eyebrow">WHAT THIS SHOWS</p>
                <p>The controlled evaluation suggests that task-side supervision can shift a model toward a lower-cost operating point without observed accuracy loss.</p>
              </article>
              <article>
                <p className="eyebrow">WHAT REMAINS TO BE TESTED</p>
                <p>
                  Generalization across models, domains, datasets, seeds and full-parameter training remains to be tested.
                </p>
              </article>
            </div>

            <FourArmStudy />
          </div>
        </section>

        <section className="section pilot-section" id="pilot" aria-labelledby="pilot-title">
          <FacetedBackdrop className="pilot-mesh" variant="pilot" />
          <div className="shell">
            <div className="split-heading reveal">
              <div>
                <p className="eyebrow">WORK WITH KEYWAN</p>
                <h2 id="pilot-title">Move a runtime behavior into the model.</h2>
              </div>
              <p>
                Keywan works with model teams to test whether a measurable runtime behavior can be learned by the model and validated against a fixed baseline.
              </p>
            </div>

            <ol className="pilot-steps reveal">
              {pilotSteps.map((step) => (
                <li key={step.number}>
                  <span>{step.number}</span>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </li>
              ))}
            </ol>

            <div className="pilot-fit reveal">
              <div>
                <p className="eyebrow">GOOD PILOT FIT</p>
                <p>
                  You control an open-weight or custom checkpoint, have a reproducible evaluation set, and face a measurable inference-cost or model-behavior problem.
                </p>
              </div>
              <a
                className="button button-primary"
                href={pilotContactUrl}
                target={externalContact ? "_blank" : undefined}
                rel={externalContact ? "noopener noreferrer" : undefined}
              >
                Discuss an external pilot <Arrow diagonal={externalContact} />
              </a>
            </div>
          </div>
        </section>

        <section className="section research-section" id="research" aria-labelledby="research-title">
          <div className="shell">
            <div className="section-heading reveal">
              <p className="eyebrow">RESEARCH</p>
              <h2 id="research-title">Research and next steps.</h2>
            </div>
            <div className="research-list reveal">
              <article>
                <span>01 · PREPRINT</span>
                <div>
                  <h3>MAVERICK preprint</h3>
                  <p>The first controlled experiment: can explicit task-side supervision alter reasoning expenditure?</p>
                  <small>
                    Accepted for presentation at IEEE DG 2026.<br />
                    Proceedings publication pending.
                  </small>
                </div>
                <span className="disabled-preprint" role="link" aria-disabled="true">Preprint available soon</span>
              </article>
              <article>
                <span>02 · DATASET</span>
                <div>
                  <h3>MAVERICK dataset</h3>
                  <p>Source tasks, metadata, reasoning targets and validation records.</p>
                  <small>1,180 validated variants · public repository</small>
                </div>
                <a href={siteConfig.datasetUrl} target="_blank" rel="noopener noreferrer">Open dataset <Arrow diagonal /></a>
              </article>
              <article className="research-next">
                <span>03 · NEXT</span>
                <div>
                  <h3>From reasoning modes to a general representation of reasoning demand.</h3>
                  <p>
                    A planned inverse mapping from natural questions to structured reasoning-demand profiles would connect task analysis with controlled generation. Further work targets richer, potentially continuous representations, larger datasets, additional domains and model families, and full-parameter training.
                  </p>
                  <small>Goal: a broader 2027 submission targeting ICML, NeurIPS or a comparable top-tier machine-learning venue.</small>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="section founder-section" id="founder" aria-labelledby="founder-title">
          <FacetedBackdrop className="founder-facet" variant="founder" />
          <div className="shell founder-layout reveal">
            <div className="founder-mark">
              <img src="/keywan-mark-lossless.webp" alt="" width="160" height="160" />
            </div>
            <div className="founder-copy">
              <p className="eyebrow">SINGLE-AUTHOR RESEARCH</p>
              <h2 id="founder-title">Built by a technical founder.</h2>
              <p>
                Keywan was founded by Daniil Gabrielyan, sole author of MAVERICK. The research began during his first undergraduate year, and he built the generation, validation, training and evaluation system end-to-end.
              </p>
              <dl>
                <div><dt>Education</dt><dd>Mathematical and Computational Science, Astana IT University.</dd></div>
                <div><dt>Research</dt><dd>IEEE DG 2026 accepted presenter.</dd></div>
              </dl>
              <div className="text-links">
                <a href={siteConfig.linkedinUrl} target="_blank" rel="noopener noreferrer">LinkedIn <Arrow diagonal /></a>
                <a href={siteConfig.githubUrl} target="_blank" rel="noopener noreferrer">GitHub <Arrow diagonal /></a>
              </div>
            </div>
          </div>
        </section>

        <section className="final-cta" id="contact" aria-labelledby="contact-title">
          <FacetedBackdrop className="final-cta-object" variant="contact" />
          <div className="shell reveal">
            <p className="eyebrow">TURN RUNTIME CONTROL INTO LEARNED BEHAVIOR</p>
            <h2 id="contact-title">Teach the behavior once.</h2>
            <div className="final-cta-actions">
              <a
                className="button button-primary"
                href={pilotContactUrl}
                target={externalContact ? "_blank" : undefined}
                rel={externalContact ? "noopener noreferrer" : undefined}
              >
                Discuss a pilot <Arrow diagonal={externalContact} />
              </a>
              <a className="button button-secondary" href="#research">Read the research <Arrow /></a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="shell footer-inner">
          <div className="footer-brand">
            <Brand />
          </div>
          <nav aria-label="Footer navigation">
            <span className="footer-preprint-disabled" role="link" aria-disabled="true"><FooterIcon name="research" /><span>Preprint available soon</span></span>
            <a href={siteConfig.datasetUrl} target="_blank" rel="noopener noreferrer"><FooterIcon name="dataset" /><span>Dataset</span></a>
            <a href={siteConfig.githubUrl} target="_blank" rel="noopener noreferrer"><FooterIcon name="github" /><span>GitHub</span></a>
            <a href={siteConfig.linkedinUrl} target="_blank" rel="noopener noreferrer"><FooterIcon name="linkedin" /><span>LinkedIn</span></a>
            <a href={pilotContactUrl} target={externalContact ? "_blank" : undefined} rel={externalContact ? "noopener noreferrer" : undefined}><FooterIcon name="contact" /><span>Contact</span></a>
          </nav>
          <p className="footer-note">Turning runtime control into learned behavior.</p>
          <p className="footer-location">Astana, Kazakhstan</p>
        </div>
      </footer>
    </>
  );
}

export default App;
